/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { makeT } from "app/client/lib/localization";
import { getLoginOrSignupUrl, getLogoutUrl } from "app/client/lib/urlUtils";
import { AppModel } from "app/client/models/AppModel";
import { DocPageModel } from "app/client/models/DocPageModel";
import { urlState } from "app/client/models/gristUrlState";
import { getAdminPanelName } from "app/client/ui/AdminPanelName";
import { manageTeamUsers } from "app/client/ui/OpenUserManager";
import { maybeAddSiteSwitcherSection } from "app/client/ui/SiteSwitcher";
import { createUserImage } from "app/client/ui/UserImage";
import * as viewport from "app/client/ui/viewport";
import { primaryButtonLink } from "app/client/ui2018/buttons";
import { mediaDeviceNotSmall, testId, theme, vars } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import {
  menu,
  menuDivider,
  menuItem,
  menuItemLink,
} from "app/client/ui2018/menus";
import { unstyledButton } from "app/client/ui2018/unstyled";
import { commonUrls, isFeatureEnabled } from "app/common/gristUrls";
import { FullUser } from "app/common/LoginSessionAPI";
import * as roles from "app/common/roles";
import { getGristConfig } from "app/common/urlUtils";

import { Disposable, dom, DomElementArg, styled } from "grainjs";

const t = makeT("AccountWidget");

/**
 * Render the user-icon that opens the account menu.
 *
 * When no user is logged in, render "Sign In" and "Sign Up" buttons.
 *
 * When no user is logged in and a template document is open, render a "Use This Template"
 * button.
 */
export class AccountWidget extends Disposable {
  constructor(private _appModel: AppModel, private _docPageModel?: DocPageModel) {
    super();
  }

  public buildDom() {
    return cssAccountWidget(
      dom.domComputed((use) => {
        const isTemplate = Boolean(this._docPageModel && use(this._docPageModel.isTemplate));
        const user = this._appModel.currentValidUser;
        if (!user && isTemplate) {
          return this._buildUseThisTemplateButton();
        }
        else if (!user) {
          // return this._buildSignInAndSignUpButtons();
        }
        else {
          return this._buildAccountMenuButton(user);
        }
      }),
      testId("dm-account"),
    );
  }

  private _buildAccountMenuButton(user: FullUser | null) {
    return cssUserIcon(
      createUserImage(user, "medium", testId("user-icon")),
      menu(() => this._makeAccountMenu(user), { placement: "bottom-end" }),
    );
  }

  // private _buildSignInAndSignUpButtons() {
  //   return [
  //     cssSigninButton(t("Sign in"),
  //       cssSigninButton.cls("-secondary"),
  //       dom.on("click", () => { this._docPageModel?.clearUnsavedChanges(); }),
  //       dom.attr("href", (use) => {
  //         // Keep the redirect param of the login URL fresh.
  //         use(urlState().state);
  //         return getLoginUrl();
  //       }),
  //       testId("user-sign-in"),
  //     ),
  //     cssSigninButton(t("Sign up"),
  //       dom.on("click", () => { this._docPageModel?.clearUnsavedChanges(); }),
  //       dom.attr("href", (use) => {
  //         // Keep the redirect param of the signup URL fresh.
  //         use(urlState().state);
  //         return getSignupUrl();
  //       }),
  //       testId("user-sign-up"),
  //     ),
  //   ];
  // }

  private _buildUseThisTemplateButton() {
    return cssUseThisTemplateButton(t("Use This Template"),
      dom.attr("href", (use) => {
        const { doc: srcDocId } = use(urlState().state);
        return getLoginOrSignupUrl({ srcDocId });
      }),
      dom.on("click", () => { this._docPageModel?.clearUnsavedChanges(); }),
      testId("dm-account-use-this-template"),
    );
  }

  /**
   * Renders the content of the account menu, with a list of available orgs, settings, and sign-out.
   * Note that `user` should NOT be anonymous (none of the items are really relevant).
   */
  private _makeAccountMenu(user: FullUser | null): DomElementArg[] {
    const currentOrg = this._appModel.currentOrg;

    // The 'Document settings' item, when there is an open document.
    const documentSettingsItem = this._docPageModel ? menuItemLink(
      urlState().setLinkUrl({ docPage: "settings" }),
      t("Document settings"),
      testId("dm-doc-settings"),
    ) : null;

    // The item to toggle mobile mode (presence of viewport meta tag).
    const mobileModeToggle = menuItem(viewport.toggleViewport,
      cssSmallDeviceOnly.cls(""),   // Only show this toggle on small devices.
      t("Toggle Mobile Mode"),
      cssCheckmark("Tick", dom.show(viewport.viewportEnabled)),
      testId("usermenu-toggle-mobile"),
    );

    if (!user) {
      return [
        menuItemLink({ href: getLoginOrSignupUrl() }, t("Sign in")),
        menuDivider(),
        documentSettingsItem,
        menuItemLink({ href: commonUrls.plans }, t("Pricing")),
        mobileModeToggle,
      ];
    }

    return [
      cssUserInfo(
        createUserImage(user, "large"),
        cssUserName(dom("span", user.name, testId("usermenu-name")),
          cssEmail(user.email, testId("usermenu-email")),
        ),
      ),
      menuItemLink(urlState().setLinkUrl({ account: "account" }), t("Profile settings"), testId("dm-account-settings")),

      documentSettingsItem,

      // Show 'Manage Team' on team sites only (not personal orgs), for install admins and owners.
      // Editors and viewers cannot manage access.
      (!this._docPageModel && currentOrg &&
        (this._appModel.isInstallAdmin() || (this._appModel.isTeamSite && roles.canEditAccess(currentOrg.access))) ?
        menuItem(() => manageTeamUsers({ org: currentOrg, user, api: this._appModel.api }),
          t("Manage Team"),
          testId("dm-org-access")) :
        null),

      this._maybeBuildBillingPageMenuItem(),
      this._maybeBuildActivationPageMenuItem(),
      this._maybeBuildAdminPanelMenuItem(),
      this._maybeBuildSupportGristButton(),

      // TODO: Uncomment when team audit logs are ready to use.
      // this._maybeBuildAuditLogsMenuItem(),

      mobileModeToggle,

      // TODO Add section ("Here right now") listing icons of other users currently on this doc.
      // (See Invision "Panels" near the bottom.)

      // KNET-SESSION-REMOVAL: Multi-account switching disabled — JWT auth supports single user per session
      // this._appModel.topAppModel.isSingleOrg || !isFeatureEnabled("multiAccounts") ? [] : [
      //   menuDivider(),
      //   menuSubHeader(dom.text(use => use(users).length > 1 ? t("Switch Accounts") : t("Accounts"))),
      //   dom.forEach(users, (_user) => {
      //     if (_user.id === user.id) { return null; }
      //     return menuItem(() => this._switchAccount(_user),
      //       cssSmallIconWrap(createUserImage(_user, "small")),
      //       cssOtherEmail(_user.email, testId("usermenu-other-email")),
      //     );
      //   }),
      //   isExternal ? null : menuItemLink({ href: getLoginUrl() }, t("Add account"), testId("dm-add-account")),
      // ],

      menuItemLink({ href: getLogoutUrl() }, t("Sign out"), testId("dm-log-out")),

      maybeAddSiteSwitcherSection(this._appModel),
    ];
  }

  private async _handleSignOut() {
    try {
      // Sign out from Supabase if configured
      const supabaseUrl = (window as any).GRIST_CONFIG?.SUPABASE_URL ||
        process.env.GRIST_SUPABASE_URL || process.env.SUPABASE_URL;
      const supabaseAnonKey = (window as any).GRIST_CONFIG?.SUPABASE_ANON_KEY ||
        process.env.GRIST_SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY;

      if (supabaseUrl && supabaseAnonKey) {
        // Dynamically import Supabase client
        const { createClient } = await import("@supabase/supabase-js");
        const cookieName = (window as any).gristConfig?.supabaseCookieName || "backend";
        const supabase = createClient(supabaseUrl, supabaseAnonKey, {
          auth: {
            storageKey: `kn-${cookieName}-auth-token`,
          },
        });
        await supabase.auth.signOut();
      }
    }
    catch (err) {
      console.warn("Failed to sign out from Supabase:", err);
    }

    // Always redirect to Grist logout URL regardless of Supabase result
    window.location.href = getLogoutUrl();
  }

  // Switch BrowserSession to use the given user for the currently loaded org.
  private async _switchAccount(user: FullUser) {
    await this._appModel.switchUser(user);
    if (urlState().state.get().doc) {
      // Document access level may have changed.
      // If it was not accessible but now is, we currently need to reload the page to get
      // a complete gristConfig for the document from the server.
      // If it was accessible but now is not, it would suffice to reconnect the web socket.
      // For simplicity, just reload from server in either case.
      // TODO: get fancier here to avoid reload.
      window.location.reload(true);
      return;
    }
    this._appModel.topAppModel.initialize();
  }

  private _maybeBuildBillingPageMenuItem() {
    const { isPersonal } = this._appModel;
    const canView = Boolean(
      this._appModel.isOwner() ||
      this._appModel.isBillingManager() ||
      this._appModel.isInstallAdmin(),
    );
    if (!canView) { return null; }

    const items: DomElementArg[] = [
      menuItemLink(urlState().setLinkUrl({ billing: "billing" }), t("Plan & Usage"), testId("dm-plan-usage")),
      menuItemLink(urlState().setLinkUrl({ usage: "usage" }), t("Usage"), testId("dm-usage")),
    ];

    // Show "Upgrade" only for personal org owners who are NOT already org admins
    // (org admins already manage team sites, so upgrade doesn't apply to them).
    const isOrgAdmin = Boolean(this._appModel.currentValidUser?.orgAdmin);
    if (isPersonal && !isOrgAdmin) {
      items.push(
        menuItem(() => this._showKNetUpgradeModal(), t("Upgrade"), testId("dm-upgrade")),
      );
    }

    return items;
  }

  private _showKNetUpgradeModal() {
    // Simple advisory modal directing users to support.
    const modal = document.createElement("div");
    modal.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;";
    const box = document.createElement("div");
    box.style.cssText = "background:white;border-radius:8px;padding:32px;max-width:480px;width:90%;font-family:sans-serif;";
    box.innerHTML = `
      <h2 style="margin:0 0 16px;font-size:18px;">Upgrade Your Plan</h2>
      <p style="margin:0 0 24px;color:#555;">To upgrade your plan or enable team features, contact
        <a href="mailto:support@knetai.com" style="color:#2563eb;">support@knetai.com</a>.
      </p>
      <button id="_knet-upgrade-close" style="background:#2563eb;color:white;border:none;border-radius:4px;padding:8px 20px;cursor:pointer;font-size:14px;">Close</button>
    `;
    modal.appendChild(box);
    document.body.appendChild(modal);
    const close = () => document.body.removeChild(modal);
    modal.addEventListener("click", (e) => { if (e.target === modal) { close(); } });
    box.querySelector("#_knet-upgrade-close")?.addEventListener("click", close);
  }

  private _maybeBuildActivationPageMenuItem() {
    const { deploymentType } = getGristConfig();
    if (deploymentType !== "enterprise" || !this._appModel.isInstallAdmin()) {
      return null;
    }

    return menuItemLink(t("Activation"), urlState().setLinkUrl({ activation: "activation" }));
  }

  private _maybeBuildAdminPanelMenuItem() {
    // Only show Admin Panel item to the installation admins.
    if (this._appModel.currentUser?.isInstallAdmin) {
      return menuItemLink(
        getAdminPanelName(),
        urlState().setLinkUrl({ adminPanel: "admin" }),
        testId("usermenu-admin-panel"),
      );
    }
  }

  private _maybeBuildSupportGristButton() {
    const { deploymentType } = getGristConfig();
    const isEnabled = (deploymentType === "core") && isFeatureEnabled("supportGrist");
    if (isEnabled) {
      return menuItemLink(t("Support Grist"), " 💛",
        { href: commonUrls.githubSponsorGristLabs, target: "_blank" },
        testId("usermenu-support-grist"),
      );
    }
  }

  // TODO: Uncomment when team audit logs are ready to use.
  // private _maybeBuildAuditLogsMenuItem() {
  //   const { deploymentType } = getGristConfig();
  //   if (
  //     !this._appModel.isOwner() ||
  //     !this._appModel.isTeamSite ||
  //     !deploymentType ||
  //     !["saas", "core", "enterprise"].includes(deploymentType)
  //   ) {
  //     return null;
  //   }

  //   return menuItemLink(
  //     t("Audit Logs"),
  //     menuAnnotate(t("New")),
  //     urlState().setLinkUrl({ auditLogs: "audit-logs" })
  //   );
  // }
}

const cssAccountWidget = styled("div", `
  display: flex;
  margin-right: 16px;
  white-space: nowrap;
`);

export const cssUserIcon = styled(unstyledButton, `
  height: 48px;
  width: 48px;
  padding: 8px;
  cursor: pointer;
  outline-offset: -3px;
`);

const cssUserInfo = styled("div", `
  padding: 12px 24px 12px 16px;
  min-width: 200px;
  display: flex;
  align-items: center;
`);

const cssUserName = styled("div", `
  margin-left: 8px;
  font-size: ${vars.mediumFontSize};
  font-weight: ${vars.headerControlTextWeight};
  color: ${theme.text};
`);

const cssEmail = styled("div", `
  margin-top: 4px;
  font-size: ${vars.smallFontSize};
  font-weight: initial;
  color: ${theme.lightText};
`);

const cssCheckmark = styled(icon, `
  flex: none;
  margin-left: 16px;
  --icon-color: ${theme.accentIcon};
`);

// Note that this css class hides the item when the device width is small (not based on viewport
// width, which may be larger). This only appropriate for when to enable the "mobile mode" toggle.
const cssSmallDeviceOnly = styled(menuItem, `
  @media ${mediaDeviceNotSmall} {
    & {
      display: none;
    }
  }
`);

// const cssSigninButton = styled(bigPrimaryButtonLink, `
//   display: flex;
//   align-items: center;
//   font-weight: 700;
//   min-height: unset;
//   height: 36px;
//   padding: 8px 16px 8px 16px;
//   font-size: ${vars.mediumFontSize};

//   &-secondary, &-secondary:hover {
//     background-color: transparent;
//     border-color: transparent;
//     color: ${theme.text};
//   }
// `);

const cssUseThisTemplateButton = styled(primaryButtonLink, `
  margin: 8px;
`);
